import { ProductType, RawGreeksDataMap, RawStatsDataMap } from '../../types';
import { fetchRawFormattedData, getFilteredGreeksData } from 'util/iVol';
import {
  fetchExerciseStyles,
  type ExerciseStylesRecord,
} from 'util/moviefoneExerciseStyles';
import useLog from '../useLog';
import { useCallback } from 'react';
import useAuth from 'hooks/auth/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { decode } from '@msgpack/msgpack';
import { DefaultService as StreamAPI } from 'gen/stream-api/requests/services.gen';
import { UseDefaultServiceGetV2DailyGreeksKeyFn } from 'gen/stream-api/queries/common';

interface UseImpliedVolatilityProps {
  bypassAccessCheck?: boolean;
}

const useImpliedVolatility = ({
  bypassAccessCheck = false,
}: UseImpliedVolatilityProps = {}) => {
  const { logError } = useLog('useImpliedVolatility');
  const { hasAccessToProduct } = useAuth();
  const queryClient = useQueryClient();
  const hasAccess =
    bypassAccessCheck || hasAccessToProduct(ProductType.IMPLIED_VOL);

  const fetchDailyGreeks = useCallback(
    async (params: {
      sym: string;
      date?: string;
      mktClose?: boolean;
    }): Promise<RawGreeksDataMap> => {
      const rawData = await queryClient.fetchQuery({
        queryKey: UseDefaultServiceGetV2DailyGreeksKeyFn(params),
        queryFn: () => StreamAPI.getV2DailyGreeks(params),
      });
      return decode(rawData as ArrayBuffer) as RawGreeksDataMap;
    },
    [queryClient],
  );

  const getCurrentGreeksData = useCallback(
    async (ticker: string): Promise<RawGreeksDataMap | null> => {
      try {
        const rawData: RawGreeksDataMap = await fetchRawFormattedData(
          hasAccess
            ? `v2/latest_greeks?sym=${encodeURIComponent(ticker)}`
            : `v2/free_latest_greeks?sym=${encodeURIComponent(ticker)}`,
        );

        const updatedRawData: RawGreeksDataMap = getFilteredGreeksData(rawData);

        return updatedRawData;
      } catch (error) {
        logError(error, 'getCurrentGreeksData');
        return null;
      }
    },
    [logError, hasAccess],
  );

  const getDailyGreeksData = useCallback(
    async (
      date: string,
      ticker: string,
      isMktOpen?: boolean,
    ): Promise<RawGreeksDataMap | null> => {
      try {
        const rawData = await fetchDailyGreeks({
          sym: ticker,
          date,
          mktClose: !(isMktOpen ?? false),
        });

        // filter out any expirations before given trade "date"
        const updatedRawData: RawGreeksDataMap = getFilteredGreeksData(
          rawData,
          date,
        );

        return updatedRawData;
      } catch (error) {
        logError(error, 'getDailyGreeksData');
        return null;
      }
    },
    [fetchDailyGreeks, logError],
  );

  const getStatisticsData = useCallback(
    async (ticker: string, date?: string): Promise<RawStatsDataMap | null> => {
      try {
        return await fetchRawFormattedData(
          `v1/${
            !hasAccess ? 'free_iv_stats' : 'iv_stats'
          }?sym=${encodeURIComponent(ticker)}${date != null ? `&date=${date}` : ''}`,
        );
      } catch (error) {
        logError(error, 'getStatisticsData');
        return null;
      }
    },
    [hasAccess, logError],
  );

  const getExerciseStyles = useCallback(
    async (ticker: string): Promise<ExerciseStylesRecord | null> =>
      fetchExerciseStyles(ticker),
    [],
  );

  return {
    getCurrentGreeksData,
    getDailyGreeksData,
    getStatisticsData,
    getExerciseStyles,
  };
};

export default useImpliedVolatility;
